import {
  ArrowRightIcon,
  CheckCircleIcon,
  CheckIcon,
  CreditCardIcon,
  DeviceMobileIcon,
  EnvelopeSimpleIcon,
  LockKeyIcon,
  PhoneIcon,
  ShieldCheckIcon,
  SpinnerIcon,
  TagIcon,
  UserIcon,
} from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Form,
  FormField,
  FormItem,
  FormLabel,
  Input,
  Label,
} from "@reactive-resume/ui";
import {
  cn,
  DEFAULT_CARD_CURRENCY,
  formatMoney,
} from "@reactive-resume/utils";
import { useKKiaPay } from "kkiapay-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import type { Country } from "react-phone-number-input";
import PhoneInput from "react-phone-number-input/max";
import "react-phone-number-input/style.css";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import {
  MOMO_PHONE_LABELS,
  momoPhoneHint,
  momoPhonePlaceholder,
  resolveMomoPhone,
} from "@/client/libs/momo-phone";
import { useAiPacks } from "@/client/services/ai-credit/api";
import {
  getCardMoneyContext,
  useAccountCurrency,
} from "@/client/services/currency/currency";
import {
  usePay,
  type InitPayDateType,
} from "@/client/services/payment/init-payment";
import { usePaymentOptions } from "@/client/services/payment/payment-options";
import { useRatePay } from "@/client/services/rate/rates";
import { useUser } from "@/client/services/user";
import { useDialog } from "@/client/stores/dialog";

const CARD_CURRENCY_LABELS: Record<string, string> = {
  EUR: "euros",
  USD: "dollars américains",
};

type PaymentMethod =
  | "card"
  | "mobile";

type PaymentSource =
  | "offer"
  | "ai-credits";

type PaymentProduct = {
  id: string;
  name: string;
  price: number;
  credits: number;
  validity: number;
  description?: string | null;
  recommended?: boolean;
};

type PaymentPayload = {
  item?: {
    id?: string;
    source?: PaymentSource;
    returnTo?: string;
    product?: PaymentProduct;
  };
};

type FormValue = {
  type: "momo" | "card";
  rate: string;
  country?: string;
  countryIso2?: Country;
  coupon?: string;
  phoneNumber: string;
};

type PaymentResponse = {
  simulated?: boolean;
  requiresRedirect?: boolean;
  redirectUrl?: string;
  link?: string;
  providerTransactionId?: string;
  trxId?: string;

  data?: {
    status?: number;
    providerTransactionId?: string;
    trxId?: string;

    intend?: {
      Amount: number;
    };

    intendid?: string;
  };
};

export const PaymentDialog = () => {
  const {
    isOpen,
    close,
    payload,
  } = useDialog("payment");

  const paymentPayload =
    payload as PaymentPayload;

  const source =
    paymentPayload?.item?.source ??
    "offer";

  const payloadPlanId =
    paymentPayload?.item?.id;

  const payloadProduct =
    paymentPayload?.item?.product;

  const { rates } = useRatePay(
    isOpen && source === "offer",
  );

  const aiPacks = useAiPacks(
    isOpen &&
      source === "ai-credits",
  );

  const { pay } = usePay();

  const {
    options: paymentOptions,
    error: paymentOptionsError,
    loading: paymentOptionsLoading,
    refetch: refetchPaymentOptions,
  } = usePaymentOptions(isOpen);

  const { user } = useUser();

  const {
    openKkiapayWidget,
    addKkiapayListener,
    removeKkiapayListener,
  } = useKKiaPay();

  /*
   * Le mode KKiaPay reste contrôlé
   * depuis les variables d’environnement.
   */
  const kkiapaySandbox =
    String(
      import.meta.env
        .VITE_KKIAPAY_SANDBOX ??
        "true",
    ).toLowerCase() !== "false";

  const kkiapayPartnerIdRef =
    useRef<string | null>(null);

  useEffect(() => {
    const successHandler = (
      response: unknown,
    ) => {
      const transactionId =
        (
          response as {
            transactionId?: string;
          }
        )?.transactionId ?? null;

      const intend =
        kkiapayPartnerIdRef.current;

      toast.success(
        "Paiement effectué — vérification en cours…",
      );

      if (!intend) {
        return;
      }

      const params =
        new URLSearchParams({
          intend,
        });

      if (transactionId) {
        params.set(
          "trxId",
          transactionId,
        );
      }

      window.location.href =
        `${
          window.location.origin
        }/client/payment-check/payment?${params.toString()}`;
    };

    const failureHandler = (
      error: unknown,
    ) => {
      const message =
        (
          error as {
            message?: string;
          }
        )?.message ??
        "Le paiement KKiaPay n'a pas abouti. Vous pouvez réessayer.";

      toast.error(message);
    };

    addKkiapayListener(
      "success",
      successHandler,
    );

    addKkiapayListener(
      "failed",
      failureHandler,
    );

    return () => {
      removeKkiapayListener(
        "success",
      );

      removeKkiapayListener(
        "failed",
      );
    };
  }, [
    addKkiapayListener,
    removeKkiapayListener,
  ]);

  const idempotencyKey =
    useRef(uuidv4());

  const [
    method,
    setMethod,
  ] = useState<PaymentMethod>(
    "card",
  );

  const [
    paymentError,
    setPaymentError,
  ] = useState("");

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  const fallbackCvPlan =
    rates?.data
      ?.filter(
        (plan) =>
          Number(plan.credit) > 0,
      )
      .find(
        (plan) =>
          plan.id === payloadPlanId,
      );

  const fallbackAiPack =
    aiPacks.data?.find(
      (pack) =>
        pack.id === payloadPlanId,
    );

  const product:
    | PaymentProduct
    | undefined =
    payloadProduct ??
    (fallbackCvPlan
      ? {
          id: fallbackCvPlan.id,
          name: fallbackCvPlan.name,
          price: fallbackCvPlan.price,
          credits:
            fallbackCvPlan.credit,
          validity:
            fallbackCvPlan.validity,
          description:
            fallbackCvPlan.description,
          recommended:
            fallbackCvPlan.isRecommended,
        }
      : fallbackAiPack
        ? {
            id: fallbackAiPack.id,
            name: fallbackAiPack.name,
            price:
              fallbackAiPack.price,
            credits:
              fallbackAiPack.aiPoints,
            validity:
              fallbackAiPack.validity,
            description:
              fallbackAiPack.description,
            recommended:
              fallbackAiPack.isRecommended,
          }
        : undefined);

  const form = useForm<FormValue>({
    defaultValues: {
      type: "card",
      rate:
        payloadPlanId ?? "",
      country: undefined,
      countryIso2: undefined,
      coupon: "",
      phoneNumber: "",
    },
  });

  const cardAvailable =
    paymentOptions?.card
      .available === true;

  const mobileAvailable =
    paymentOptions?.mobilePayment
      .available === true;

  const mobileCountryIso2 =
    mobileAvailable &&
    paymentOptions?.countryCode
      ? (paymentOptions.countryCode as Country)
      : undefined;

  const mobilePhoneCountries =
    useMemo<Country[]>(
      () =>
        mobileCountryIso2
          ? [mobileCountryIso2]
          : [],
      [mobileCountryIso2],
    );

  useEffect(() => {
    if (
      !isOpen ||
      !paymentOptions
    ) {
      return;
    }

    const initialMethod:
      PaymentMethod =
      mobileAvailable
        ? "mobile"
        : "card";

    form.reset({
      type:
        initialMethod === "mobile"
          ? "momo"
          : "card",

      rate:
        payloadPlanId ?? "",

      country:
        paymentOptions
          .mobilePayment
          .providerCountryCode ??
        undefined,

      countryIso2:
        mobileCountryIso2,

      coupon: "",
      phoneNumber: "",
    });

    setMethod(initialMethod);
    setPaymentError("");
    setSubmitting(false);

    idempotencyKey.current =
      uuidv4();
  }, [
    isOpen,
    payloadPlanId,
    form,
    paymentOptions,
    mobileAvailable,
    mobileCountryIso2,
  ]);

  const { context: accountContext } =
    useAccountCurrency();

  // Carte : devise et taux du débit Stripe. Mobile : devise du pays du compte.
  const paymentContext = useMemo(() => {
    if (
      method === "card" &&
      paymentOptions
    ) {
      return (
        getCardMoneyContext(
          paymentOptions.card.currencyCode,
          accountContext?.locale ?? "fr-FR",
        ) ?? accountContext
      );
    }

    return accountContext;
  }, [
    method,
    paymentOptions,
    accountContext,
  ]);

  const priceDisplay =
    useMemo(() => {
      if (
        !product ||
        !paymentContext
      ) {
        return {
          total: "—",
          fees: "—",
        };
      }

      return {
        total: formatMoney(
          product.price,
          paymentContext,
        ),
        fees: formatMoney(
          0,
          paymentContext,
        ),
      };
    }, [
      product,
      paymentContext,
    ]);

  const cardCurrencyCode =
    paymentOptions?.card
      .currencyCode ??
    DEFAULT_CARD_CURRENCY;

  const cardCurrencyLabel = `${
    CARD_CURRENCY_LABELS[
      cardCurrencyCode
    ] ?? cardCurrencyCode
  } (${cardCurrencyCode})`;

  const selectMethod = (
    nextMethod: PaymentMethod,
  ) => {
    if (
      nextMethod === "mobile" &&
      !mobileAvailable
    ) {
      return;
    }

    if (
      nextMethod === "card" &&
      !cardAvailable
    ) {
      return;
    }

    setMethod(nextMethod);
    setPaymentError("");

    form.setValue(
      "type",
      nextMethod === "card"
        ? "card"
        : "momo",
    );
  };

  const openWidget = (
    price: number,
    partnerId: string,
  ) => {
    kkiapayPartnerIdRef.current =
      partnerId;

    openKkiapayWidget({
      amount: price,
      position: "right",
      theme: "green",

      key:
        import.meta.env
          .VITE_KKIAPAY_API_KEY,

      sandbox:
        kkiapaySandbox,

      partnerId,

      callback:
        `${
          window.location.origin
        }/client/payment-check/payment?intend=${partnerId}`,
    });
  };

  const onSubmit = async (
    values: FormValue,
  ) => {
    if (!product) {
      return;
    }

    if (
      values.type === "momo" &&
      !mobileAvailable
    ) {
      setPaymentError(
        "Le paiement mobile n’est pas disponible pour votre pays.",
      );

      return;
    }

    if (
      values.type === "card" &&
      !cardAvailable
    ) {
      setPaymentError(
        "Le paiement par carte est momentanément indisponible.",
      );

      return;
    }

    setSubmitting(true);
    setPaymentError("");

    try {
      const inputCode =
        values.coupon?.trim();

      const ambassadorCode =
        inputCode
          ?.toUpperCase()
          .startsWith("AMB-")
          ? inputCode.toUpperCase()
          : undefined;

      const couponCode =
        inputCode &&
        !ambassadorCode
          ? inputCode
          : undefined;

      const common = {
        rate: product.id,
        couponCode,
        ambassadorCode,

        idempotencykey:
          idempotencyKey.current,
      };

      const momoPhone =
        values.type === "momo"
          ? resolveMomoPhone(
              values.phoneNumber,
            )
          : null;

      if (
        values.type === "momo" &&
        !momoPhone
      ) {
        setPaymentError(
          "Le numéro Mobile Money ne correspond pas au pays enregistré sur votre compte.",
        );

        return;
      }

      const providerCountryCode =
        paymentOptions
          ?.mobilePayment
          .providerCountryCode;

      if (
        values.type === "momo" &&
        momoPhone &&
        providerCountryCode &&
        momoPhone.countryIso3 !==
          providerCountryCode
      ) {
        setPaymentError(
          "Utilisez un numéro Mobile Money correspondant au pays enregistré sur votre compte.",
        );

        return;
      }

      const request:
        InitPayDateType =
        values.type === "momo" &&
        momoPhone
          ? {
              ...common,
              type: "momo",

              countryCode:
                providerCountryCode ??
                momoPhone.countryIso3,

              phoneNumber:
                momoPhone.e164,
            }
          : {
              ...common,
              type: "card",
              currency:
                cardCurrencyCode,
            };

      const response =
        await pay(request);

      const responseData =
        response?.data as
          | PaymentResponse
          | undefined;

      const redirectUrl =
        responseData
          ?.redirectUrl ??
        responseData?.link;

      const transactionId =
        responseData?.data
          ?.providerTransactionId ??
        responseData
          ?.providerTransactionId ??
        responseData?.data
          ?.trxId ??
        responseData?.trxId ??
        (redirectUrl
          ? new URL(
              redirectUrl,
            ).searchParams.get(
              "trxId",
            )
          : null);

      if (transactionId) {
        localStorage.setItem(
          "trxId",
          String(transactionId),
        );
      }

      if (
        responseData?.simulated ===
          true &&
        responseData
          .requiresRedirect ===
          false &&
        responseData.data
          ?.status === 1
      ) {
        idempotencyKey.current =
          uuidv4();

        close();
        window.location.reload();

        return;
      }

      if (redirectUrl) {
        window.location.href =
          redirectUrl;

        return;
      }

      const paymentData =
        responseData?.data;

      if (
        !paymentData?.intend ||
        !paymentData.intendid
      ) {
        throw new Error(
          "La réponse du paiement est incomplète.",
        );
      }

      close();

      openWidget(
        paymentData.intend
          .Amount,
        paymentData.intendid,
      );
    } catch (error) {
      const paymentErrorData =
        error as {
          response?: {
            data?: {
              message?: string;
            };
          };

          message?: string;
        };

      const message =
        paymentErrorData
          .response?.data
          ?.message ??
        paymentErrorData.message ??
        "";

      setPaymentError(
        message.includes("carte")
          ? mobileAvailable
            ? "Le paiement par carte est momentanément indisponible. Vous pouvez utiliser le paiement mobile."
            : "Le paiement par carte est momentanément indisponible. Veuillez réessayer plus tard."
          : message ||
              "Une erreur est survenue. Veuillez réessayer.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const phoneNumber =
    form.watch("phoneNumber");

  const countryIso2 =
    form.watch("countryIso2");

  const resolvedPhone =
    resolveMomoPhone(
      phoneNumber,
    );

  const validPhone = Boolean(
    resolvedPhone &&
      paymentOptions
        ?.mobilePayment
        .providerCountryCode &&
      resolvedPhone.countryIso3 ===
        paymentOptions
          .mobilePayment
          .providerCountryCode,
  );

  const isCard =
    method === "card";

  const selectedMethodAvailable =
    isCard
      ? cardAvailable
      : mobileAvailable;

  const canSubmit =
    Boolean(product) &&
    Boolean(paymentOptions) &&
    !paymentOptionsLoading &&
    !submitting &&
    selectedMethodAvailable &&
    (isCard || validPhone);

  const productLabel =
    source === "ai-credits"
      ? "Crédits IA"
      : "Crédits CV";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          close();
        }
      }}
    >
      <DialogContent
        className={[
          "flex w-[calc(100vw-1rem)] max-w-6xl flex-col",
          "max-h-[calc(100dvh-1rem)] overflow-hidden",
          "rounded-2xl border-0 bg-[#f8f7ff] p-0 shadow-2xl",
          "sm:w-[calc(100vw-2rem)]",
          "xl:h-[min(850px,calc(100dvh-2rem))]",
        ].join(" ")}
      >
        <header className="relative shrink-0 bg-white px-4 py-3 text-center sm:px-7 sm:py-4">
          <img
            src="/logo/mon-cv-pro_logo.svg"
            alt="Mon CV Pro"
            className="absolute left-5 top-4 hidden h-11 w-auto md:block"
          />

          <DialogTitle className="text-xl font-black tracking-tight text-[#071451] sm:text-2xl">
            Finalisez votre paiement
          </DialogTitle>

          <DialogDescription className="mt-0.5 text-xs text-indigo-900/65">
            {product
              ? `Plus qu’une étape pour obtenir vos ${product.credits.toLocaleString(
                  "fr-FR",
                )} ${productLabel}.`
              : "Chargement de votre sélection…"}
          </DialogDescription>

          <div className="mx-auto mt-3 flex max-w-lg items-center justify-center gap-2 text-[10px] font-bold sm:text-xs">
            <span className="grid size-6 place-items-center rounded-full bg-emerald-500 text-white">
              <CheckIcon
                size={12}
                weight="bold"
              />
            </span>

            <span className="text-[#071451]">
              Pack
            </span>

            <span className="h-0.5 w-8 bg-emerald-400 sm:w-20" />

            <span className="grid size-6 place-items-center rounded-full bg-indigo-600 text-white">
              2
            </span>

            <span className="text-indigo-700">
              Paiement
            </span>

            <span className="h-0.5 w-8 bg-slate-200 sm:w-20" />

            <span className="grid size-6 place-items-center rounded-full bg-slate-200 text-slate-500">
              3
            </span>

            <span className="hidden text-slate-400 sm:inline">
              Confirmation
            </span>
          </div>
        </header>

        {!product ||
        paymentOptionsLoading ? (
          <div className="grid min-h-64 flex-1 place-items-center">
            <div className="flex flex-col items-center gap-3 text-center">
              <SpinnerIcon
                size={28}
                className="animate-spin text-indigo-600"
              />

              <p className="text-xs font-medium text-slate-500">
                Préparation des moyens de paiement disponibles…
              </p>
            </div>
          </div>
        ) : paymentOptionsError ||
          !paymentOptions ? (
          <div className="grid min-h-64 flex-1 place-items-center p-5">
            <div className="w-full max-w-md rounded-2xl border border-rose-200 bg-white p-6 text-center shadow-sm">
              <h2 className="text-sm font-bold text-rose-700">
                Impossible de charger les moyens de paiement
              </h2>

              <p className="mt-2 text-xs leading-5 text-slate-500">
                Vérifiez votre connexion puis
                réessayez. Aucun paiement ne sera
                lancé sans cette vérification.
              </p>

              <Button
                type="button"
                variant="outline"
                className="mt-4 h-10 rounded-xl border-rose-200 px-5 text-xs font-bold text-rose-700"
                onClick={() => {
                  void refetchPaymentOptions();
                }}
              >
                Réessayer
              </Button>
            </div>
          </div>
        ) : (
          <div
            className={[
              "min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 sm:p-4",
              "[scrollbar-color:#6366f1_#eef2ff]",
              "[scrollbar-width:thin]",
              "[&::-webkit-scrollbar]:w-1.5",
              "[&::-webkit-scrollbar-track]:rounded-full",
              "[&::-webkit-scrollbar-track]:bg-indigo-50",
              "[&::-webkit-scrollbar-thumb]:rounded-full",
              "[&::-webkit-scrollbar-thumb]:bg-indigo-400",
              "[&::-webkit-scrollbar-thumb:hover]:bg-indigo-500",
            ].join(" ")}
          >
            <div className="mx-auto grid min-h-0 max-w-[1280px] gap-3 xl:grid-cols-[minmax(0,1.7fr)_minmax(300px,0.82fr)] xl:items-start">
              <main className="min-h-0 space-y-3">
                <section className="rounded-2xl border border-slate-200 bg-white p-3.5 sm:p-4">
                  <h2 className="text-sm font-bold text-[#071451] sm:text-base">
                    Mode de paiement
                  </h2>

                  <div className="mt-3 grid grid-cols-2 gap-2.5">
                    <button
                      type="button"
                      aria-pressed={isCard}
                      disabled={!cardAvailable}
                      onClick={() => {
                        selectMethod(
                          "card",
                        );
                      }}
                      className={cn(
                        "relative flex min-h-[88px] flex-col items-center justify-center gap-1.5 rounded-xl border p-2 text-center transition",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",

                        isCard
                          ? "border-indigo-600 bg-indigo-50/50 text-indigo-700 ring-1 ring-indigo-100"
                          : "border-slate-200 bg-white text-[#071451] hover:border-indigo-200",

                        !cardAvailable &&
                          "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 opacity-65 hover:border-slate-200",
                      )}
                    >
                      <span
                        className={cn(
                          "absolute right-2 top-2 grid size-4 place-items-center rounded-full border",

                          isCard
                            ? "border-transparent bg-emerald-500 text-white"
                            : "border-slate-300 bg-white",
                        )}
                      >
                        {isCard && (
                          <CheckIcon
                            size={9}
                            weight="bold"
                          />
                        )}
                      </span>

                      <CreditCardIcon
                        aria-hidden
                        size={25}
                        weight="duotone"
                        className="text-indigo-600"
                      />

                      <span className="text-[10px] font-bold sm:text-xs">
                        Carte bancaire
                      </span>

                      {!cardAvailable && (
                        <span className="text-[8px] font-semibold text-slate-400">
                          Temporairement indisponible
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      aria-pressed={!isCard}
                      disabled={!mobileAvailable}
                      onClick={() => {
                        selectMethod(
                          "mobile",
                        );
                      }}
                      className={cn(
                        "relative flex min-h-[88px] flex-col items-center justify-center gap-1.5 rounded-xl border p-2 text-center transition",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",

                        !isCard
                          ? "border-emerald-500 bg-emerald-50/50 text-emerald-700 ring-1 ring-emerald-100"
                          : "border-slate-200 bg-white text-[#071451] hover:border-emerald-200",

                        !mobileAvailable &&
                          "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 opacity-65 hover:border-slate-200",
                      )}
                    >
                      <span
                        className={cn(
                          "absolute right-2 top-2 grid size-4 place-items-center rounded-full border",

                          !isCard
                            ? "border-transparent bg-emerald-500 text-white"
                            : "border-slate-300 bg-white",
                        )}
                      >
                        {!isCard && (
                          <CheckIcon
                            size={9}
                            weight="bold"
                          />
                        )}
                      </span>

                      <DeviceMobileIcon
                        aria-hidden
                        size={25}
                        weight="duotone"
                        className="text-emerald-600"
                      />

                      <span className="text-[10px] font-bold sm:text-xs">
                        Paiement mobile
                      </span>

                      {!mobileAvailable && (
                        <span className="text-[8px] font-semibold text-slate-400">
                          Non disponible dans votre pays
                        </span>
                      )}
                    </button>
                  </div>

                  {paymentOptions.countryNotProvided && (
                    <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] leading-4 text-amber-800">
                      <CreditCardIcon
                        aria-hidden
                        size={15}
                        className="mt-0.5 shrink-0"
                      />

                      Votre pays n’est pas renseigné.
                      Le paiement sera effectué par
                      carte bancaire en {cardCurrencyLabel}.
                    </div>
                  )}

                  {!paymentOptions.countryNotProvided &&
                    !mobileAvailable && (
                      <div className="mt-3 flex items-start gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-[10px] leading-4 text-indigo-900">
                        <CreditCardIcon
                          aria-hidden
                          size={15}
                          className="mt-0.5 shrink-0"
                        />

                        Le paiement mobile n’est pas
                        disponible dans votre pays. Le
                        paiement se fera par carte
                        bancaire, en {cardCurrencyLabel}.
                      </div>
                    )}
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-3.5 sm:p-4">
                  <div className="flex items-center gap-3">
                    <span
                      className={cn(
                        "grid size-10 shrink-0 place-items-center rounded-xl",

                        isCard
                          ? "bg-indigo-50 text-indigo-600"
                          : "bg-emerald-50 text-emerald-600",
                      )}
                    >
                      {isCard ? (
                        <CreditCardIcon
                          size={21}
                          weight="duotone"
                        />
                      ) : (
                        <DeviceMobileIcon
                          size={21}
                          weight="duotone"
                        />
                      )}
                    </span>

                    <div>
                      <h2 className="text-sm font-bold text-[#071451]">
                        Paiement par{" "}
                        {isCard
                          ? "carte bancaire"
                          : "Mobile Money"}
                      </h2>

                      <p className="mt-0.5 text-[10px] leading-4 text-slate-500">
                        {isCard
                          ? "Vous serez redirigé vers notre partenaire de paiement sécurisé."
                          : "Le pays de votre compte est présélectionné. Saisissez le numéro Mobile Money correspondant."}
                      </p>
                    </div>
                  </div>

                  <Form {...form}>
                    <form
                      className="mt-3 space-y-2.5"
                      onSubmit={form.handleSubmit(
                        onSubmit,
                      )}
                    >
                      <div className="grid gap-2.5 sm:grid-cols-2">
                        <div>
                          <Label className="text-[10px] font-bold text-[#071451]">
                            Nom du titulaire
                          </Label>

                          <div className="relative mt-1">
                            <UserIcon
                              size={15}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            />

                            <Input
                              readOnly
                              value={
                                user?.name ??
                                ""
                              }
                              placeholder="Nom complet"
                              className="h-10 rounded-xl border-slate-200 bg-slate-50 pl-9 text-xs"
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-[10px] font-bold text-[#071451]">
                            E-mail pour le reçu
                          </Label>

                          <div className="relative mt-1">
                            <EnvelopeSimpleIcon
                              size={15}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            />

                            <Input
                              readOnly
                              value={
                                user?.email ??
                                ""
                              }
                              placeholder="Adresse e-mail"
                              className="h-10 rounded-xl border-slate-200 bg-slate-50 pl-9 text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      {!isCard && (
                        <FormField
                          name="phoneNumber"
                          control={
                            form.control
                          }
                          render={({
                            field,
                          }) => (
                            <FormItem className="space-y-1">
                              <FormLabel className="text-[10px] font-bold text-[#071451]">
                                Numéro Mobile Money
                              </FormLabel>

                              <PhoneInput
                                {...field}
                                required
                                international
                                countryCallingCodeEditable={
                                  false
                                }
                                defaultCountry={
                                  mobileCountryIso2
                                }
                                countries={
                                  mobilePhoneCountries
                                }
                                labels={
                                  MOMO_PHONE_LABELS
                                }
                                placeholder={momoPhonePlaceholder(
                                  countryIso2,
                                )}
                                inputComponent={
                                  Input
                                }
                                className={[
                                  "[&_.PhoneInputCountry]:h-10",
                                  "[&_.PhoneInputCountry]:rounded-l-xl",
                                  "[&_.PhoneInputCountry]:border",
                                  "[&_.PhoneInputCountry]:border-r-0",
                                  "[&_.PhoneInputCountry]:border-slate-200",
                                  "[&_.PhoneInputCountry]:bg-slate-50",
                                  "[&_.PhoneInputCountry]:px-3",
                                  "[&_.PhoneInputCountrySelect]:pointer-events-none",
                                  "[&_.PhoneInputCountrySelectArrow]:hidden",
                                  "[&_.PhoneInputInput]:h-10",
                                  "[&_.PhoneInputInput]:rounded-r-xl",
                                  "[&_.PhoneInputInput]:border",
                                  "[&_.PhoneInputInput]:border-slate-200",
                                  "[&_.PhoneInputInput]:px-3",
                                  "[&_.PhoneInputInput]:text-xs",
                                ].join(
                                  " ",
                                )}
                                onChange={(
                                  value,
                                ) => {
                                  field.onChange(
                                    value ??
                                      "",
                                  );

                                  const resolved =
                                    resolveMomoPhone(
                                      value ??
                                        "",
                                    );

                                  if (
                                    resolved
                                  ) {
                                    form.setValue(
                                      "countryIso2",
                                      mobileCountryIso2,
                                      {
                                        shouldDirty:
                                          false,
                                      },
                                    );

                                    form.setValue(
                                      "country",
                                      paymentOptions
                                        .mobilePayment
                                        .providerCountryCode ??
                                        undefined,
                                      {
                                        shouldDirty:
                                          false,
                                      },
                                    );
                                  }
                                }}
                              />

                              <p className="text-[10px] leading-4 text-slate-500">
                                {momoPhoneHint(
                                  countryIso2,
                                )}{" "}
                                Le pays est défini depuis
                                votre profil.
                              </p>
                            </FormItem>
                          )}
                        />
                      )}

                      <FormField
                        name="coupon"
                        control={
                          form.control
                        }
                        render={({
                          field,
                        }) => (
                          <FormItem className="space-y-1">
                            <FormLabel className="text-[10px] font-bold text-[#071451]">
                              Code promo{" "}
                              <span className="font-normal text-slate-400">
                                (facultatif)
                              </span>
                            </FormLabel>

                            <div className="relative">
                              <TagIcon
                                size={15}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                              />

                              <Input
                                {...field}
                                value={
                                  field.value ??
                                  ""
                                }
                                placeholder="Ajouter un code promo"
                                className="h-10 rounded-xl border-slate-200 pl-9 text-xs uppercase"
                              />
                            </div>
                          </FormItem>
                        )}
                      />

                      {!isCard && (
                        <div className="flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[10px] leading-4 text-emerald-800">
                          <PhoneIcon
                            aria-hidden
                            size={15}
                            className="mt-0.5 shrink-0"
                          />

                          Après validation, confirmez la
                          transaction depuis votre téléphone
                          Mobile Money.
                        </div>
                      )}

                      {paymentError && (
                        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-[10px] text-rose-600">
                          {paymentError}
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={
                          !canSubmit
                        }
                        className="h-10 w-full rounded-xl bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 text-xs font-bold text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-45"
                      >
                        {submitting ? (
                          <>
                            <SpinnerIcon
                              size={15}
                              className="animate-spin"
                            />

                            Traitement…
                          </>
                        ) : (
                          <>
                            Payer{" "}
                            {
                              priceDisplay.total
                            }

                            <ArrowRightIcon
                              size={15}
                              weight="bold"
                            />
                          </>
                        )}
                      </Button>
                    </form>
                  </Form>
                </section>
              </main>

              <aside className="space-y-3">
                <section className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-2.5">
                    <span className="grid size-9 place-items-center rounded-full bg-indigo-100 text-indigo-700">
                      <CheckCircleIcon
                        size={19}
                        weight="fill"
                      />
                    </span>

                    <h2 className="text-sm font-bold text-[#071451]">
                      Récapitulatif
                    </h2>
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-2">
                    <span className="rounded-full bg-indigo-700 px-3 py-1 text-[9px] font-bold uppercase text-white">
                      {product.credits.toLocaleString(
                        "fr-FR",
                      )}{" "}
                      {productLabel}
                    </span>

                    {product.recommended && (
                      <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[8px] font-bold uppercase text-emerald-700">
                        Populaire
                      </span>
                    )}
                  </div>

                  <h3 className="mt-3 text-sm font-bold text-[#071451]">
                    {product.name}
                  </h3>

                  <p className="mt-1 text-[10px] text-slate-500">
                    {source ===
                    "ai-credits"
                      ? "Utilisables dans le constructeur de CV"
                      : `Offre CV valable ${product.validity} jours`}
                  </p>

                  <div className="mt-4 space-y-2 border-y border-slate-100 py-3 text-xs text-slate-600">
                    <div className="flex justify-between gap-4">
                      <span>
                        Sous-total
                      </span>

                      <strong className="text-[#071451]">
                        {
                          priceDisplay.total
                        }
                      </strong>
                    </div>

                    <div className="flex justify-between gap-4">
                      <span>
                        Frais de paiement
                      </span>

                      <strong className="text-[#071451]">
                        {
                          priceDisplay.fees
                        }
                      </strong>
                    </div>
                  </div>

                  <div className="mt-3 flex items-end justify-between gap-3">
                    <span className="text-sm font-bold text-[#071451]">
                      Total à payer
                    </span>

                    <strong className="text-lg font-black text-indigo-700">
                      {
                        priceDisplay.total
                      }
                    </strong>
                  </div>

                  <div className="mt-4 flex items-center justify-center gap-2 text-[10px] font-medium text-slate-500">
                    <LockKeyIcon
                      aria-hidden
                      size={14}
                    />

                    Transaction protégée et chiffrée
                  </div>
                </section>

                <section className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm">
                  <ShieldCheckIcon
                    aria-hidden
                    size={34}
                    weight="duotone"
                    className="shrink-0 text-emerald-500"
                  />

                  <div>
                    <h2 className="text-xs font-bold text-[#071451]">
                      Paiement sécurisé
                    </h2>

                    <p className="mt-1 text-[10px] leading-4 text-slate-500">
                      Vos informations sont protégées
                      et ne sont jamais stockées par
                      Mon CV Pro.
                    </p>
                  </div>
                </section>
              </aside>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};